import { OrbitControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import React, { FunctionComponent, useRef, useEffect, useState } from 'react';
import { Object3D, Object3DEventMap, PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { IMode, useDataStore, useSceneStore } from '../zustand';
import { IRoot, ISceneComp, ITrackingTypes } from './r3f-components/component-data-structure';
import { Jute } from '..';
import { initialiseWebEmbed } from './r3f-components/component-data-structure/types/webEmbed';
import { fitCameraToObjects } from './r3f-components/utils/fitCameraToObjects';
import { useGetActiveTrackingType } from './hoc/hocCustomHooks';

// const MAX_WOBBLE_COUNT = 1;
// const WOBBLE_RADIANS = MathUtils.degToRad(8);
// const WOBBLE_SPEED_FACTOR = 0.9;
// const ROTATION_MATRIX = new Matrix4();

const ZOOM_VARIATION_PERCENT = 25;

const OrbitControlsWrapper: FunctionComponent = () => {
	const [focused, setFocused] = useState(false);
	const { camera, scene: threeScene, gl } = useThree();
	const orbitControlsRef = useRef<OrbitControlsImpl>(null);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const mode = useDataStore(({ mode }) => mode);
	const rootId = useDataStore(({ rootId }) => rootId);
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	const activeTrackingType = useGetActiveTrackingType();
	const { cameraPosition, orbitTarget, enableZoom, enablePan, dampingFactor, autoRotateSpeed, verticalOrbit, horizontalOrbit, hintGraphic } = useDataStore(
		({ webEmbed }) => webEmbed) || initialiseWebEmbed();

	// const isWobblingRef = useRef(false);
	// const wobbleCountRef = useRef<number>(0);
	// const oldAngleRef = useRef(0);

	useEffect(() => {
		if (mode !== IMode.nonar || !componentsById || !rootId || !orbitControlsRef.current) return;

		const firstSceneId = (componentsById?.[rootId] as IRoot)?.children[0];
		const activeSceneIsFaceTracking = (componentsById?.[activeSceneId || firstSceneId] as ISceneComp)?.trackingType === ITrackingTypes.face;
		const activeSceneIsNoTrackingScreen = (componentsById?.[activeSceneId || firstSceneId] as ISceneComp)?.trackingType === ITrackingTypes.noTrackingScreen;

		if (!cameraPosition && !orbitTarget && !activeSceneIsFaceTracking && !activeSceneIsNoTrackingScreen) {
			// if no initial camera position and orbit target zoom camera to fit all objects of active scene
			const activeThreeScene = threeScene.getObjectByName(activeSceneId || firstSceneId) as Object3D<Object3DEventMap>;
			fitCameraToObjects(camera as PerspectiveCamera, orbitControlsRef.current, [activeThreeScene], 1.2);
		} else if (!cameraPosition && !orbitTarget && activeSceneIsFaceTracking) {
			camera.position.set(0, 0, 3) 
			orbitControlsRef.current.target.set(0, 0, 0);
			camera.updateProjectionMatrix();
		} else {
			// otherwise set custom initial orbit target and camera position
			if (orbitTarget) orbitControlsRef.current.target.set(...orbitTarget);
			if (cameraPosition) camera.position.set(...cameraPosition);
			camera.updateProjectionMatrix();
			orbitControlsRef.current.update();
		}

		if (enableZoom && activeTrackingType !== ITrackingTypes.content360) {
			const zoomVariation = ZOOM_VARIATION_PERCENT / 100;
			const initialCameraDistance = camera.position.distanceTo(new Vector3(...(orbitTarget || [0, 0, 0])));
			orbitControlsRef.current.minDistance = initialCameraDistance - initialCameraDistance * zoomVariation;
			orbitControlsRef.current.maxDistance = initialCameraDistance + initialCameraDistance * zoomVariation;
		}

		/*
		 ** If vertical orbit or horizontal orbit is set we need to get the current polar / azimuth angle
		 ** and use this as both the min and max to lock the respective angle to the current value
		 ** otherwise it resets camera to ( for example ) zero
		 */

		if (!verticalOrbit) {
			const polarAngle = orbitControlsRef?.current?.getPolarAngle() || 0;
			orbitControlsRef.current.minPolarAngle = polarAngle;
			orbitControlsRef.current.maxPolarAngle = polarAngle;
		}

		if (!horizontalOrbit && !autoRotateSpeed) {
			const azimuthAngle = orbitControlsRef?.current?.getAzimuthalAngle() || 0;
			orbitControlsRef.current.minAzimuthAngle = azimuthAngle;
			orbitControlsRef.current.maxAzimuthAngle = azimuthAngle;
		}

		orbitControlsRef.current.update();
	}, [activeSceneId, componentsById, rootId, camera, threeScene, mode, cameraPosition, orbitTarget, enableZoom, horizontalOrbit, verticalOrbit, autoRotateSpeed]);

	useEffect(() => {
		// isWobblingRef.current = true;
		const body = document.getElementsByTagName('body')[0];
		if (hintGraphic) Jute.showWebembedHintGraphic(true, document);

		const focusHandler = () => {
			Jute.showWebembedHintGraphic(false, document);
			setFocused(true);
		};

		const blurHandler = (e: FocusEvent) => {
			if ((e as any)?.relatedTarget?.id === 'designer-embed-ar-button' || (e as any)?.relatedTarget?.id === 'designer-embed-ar-button-span') return;
			//if (hintGraphic) Jute.showWebembedHintGraphic(true, document)
			setFocused(false);
		};

		gl.domElement.addEventListener('focus', focusHandler);

		body.setAttribute('tabindex', '1');
		body.addEventListener('focus', focusHandler);
		body.addEventListener('blur', blurHandler);

		return () => {
			body.removeEventListener('focus', focusHandler);
			gl.domElement.removeEventListener('focus', focusHandler);
			body.removeEventListener('blur', blurHandler);
		};
	}, [hintGraphic]);

	// useFrame(({clock}) => {
	//     if (!isWobblingRef.current || pointerDownRef.current) return;
	//     const wobbleAngle = Math.sin( clock.elapsedTime * WOBBLE_SPEED_FACTOR );
	//     const deltaAngle = oldAngleRef.current - wobbleAngle;
	//     if ( oldAngleRef.current < 0 && wobbleAngle > 0 ) {
	//         wobbleCountRef.current = wobbleCountRef.current + 1
	//     }
	//     if (
	//         oldAngleRef.current < 0 &&
	//         wobbleAngle < 0 &&
	//         oldAngleRef.current < wobbleAngle &&
	//         wobbleCountRef.current === MAX_WOBBLE_COUNT
	//     ) {
	//         isWobblingRef.current = false;
	//         return;
	//     }
	//     ROTATION_MATRIX.makeRotationY(deltaAngle * WOBBLE_RADIANS);
	//     orbitCamera.applyMatrix4(ROTATION_MATRIX)
	//     oldAngleRef.current = wobbleAngle;
	// })

	return (
		<OrbitControls
			ref={orbitControlsRef}
			enabled={mode === IMode.nonar}
			enableZoom={focused && enableZoom && activeTrackingType !== ITrackingTypes.content360}
			enablePan={focused && enablePan}
			enableDamping={!!dampingFactor}
			dampingFactor={dampingFactor}
			autoRotate={!!autoRotateSpeed}
			autoRotateSpeed={autoRotateSpeed}
		/>
	);
};

OrbitControlsWrapper.displayName = 'OrbitControlsWrapper';
export default React.memo(OrbitControlsWrapper);
